/**
 * cropPatterns — runtime генерация паттернов заливки для phase-based crop fill.
 *
 * Per spec v1.3 + founder feedback 2026-05-04 (v6 «По цвету культуры»):
 *   - Запланировано (planned): полупрозрачный фон + диагональные полосы цвета культуры
 *   - Растёт (growing): сплошная заливка (это НЕ паттерн, а fill-color)
 *   - Убрано (harvested): полупрозрачный фон + точки цвета культуры (founder
 *     2026-05-04 — раньше были чёрный фон + полосы стерни, заменили на точки
 *     для лучшего разделения «растёт vs убрано» визуально)
 *
 * Паттерн генерируется один раз per crop через OffscreenCanvas (32×32 px tile)
 * и регистрируется в map.addImage(`crop-stripe-${crop.id}`) / `crop-dots-${crop.id}`.
 *
 * Иконки культур (icon_url из reference.crops) загружаются асинхронно и
 * добавляются как `crop-icon-${crop.id}`.
 */
import type { Map as MLMap } from 'maplibre-gl';
import type { Crop } from './useCropsReference';

const PATTERN_SIZE = 32;

/** Получить 2d-контекст 32×32 (Offscreen или fallback). Внутренний хелпер. */
function get2dCtx(size: number): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(size, size);
    const c = canvas.getContext('2d');
    if (!c) throw new Error('OffscreenCanvas 2d context unavailable');
    return c;
  }
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const c = canvas.getContext('2d');
  if (!c) throw new Error('Canvas 2d context unavailable');
  return c;
}

/**
 * Генерирует ImageData 32×32 px с диагональными полосами цвета культуры
 * на полупрозрачном фоне. Используется для phase=planned (запланировано).
 *
 * @param cropColor — hex цвет, например "#22C55E"
 */
export function generateStripePattern(cropColor: string): ImageData {
  const size = PATTERN_SIZE;
  const ctx = get2dCtx(size);

  // Background — полупрозрачный белый (как было для planned), чтобы спутник/
  // basemap просвечивал. Цвет культуры — только в полосах.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.fillRect(0, 0, size, size);

  // Diagonal stripes
  ctx.strokeStyle = cropColor;
  ctx.lineWidth = 6;
  ctx.lineCap = 'square';
  for (let i = -size; i < size * 2; i += 12) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + size, size);
    ctx.stroke();
  }

  return ctx.getImageData(0, 0, size, size);
}

/**
 * Генерирует ImageData 32×32 px с точками цвета культуры на полупрозрачном фоне.
 * Используется для phase=harvested (убрано) — founder feedback 2026-05-04 v6:
 * «убрана → точечная (цветные точки)». Полностью заменил старый stubble-паттерн
 * (чёрный фон + полосы) — он визуально не отличался от stripe-pattern.
 *
 * Раскладка: 4×4 grid точек (16 точек) с offset для визуального tile'инга
 * без видимых швов. Радиус 2px (~6-7% от tile area), хорошая плотность на
 * любом zoom без слияния.
 */
export function generateCropDotsPattern(cropColor: string): ImageData {
  const size = PATTERN_SIZE;
  const ctx = get2dCtx(size);

  // Background — полупрозрачный белый, как у planned-stripe для визуальной
  // когерентности. Цвет культуры читается через точки.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.fillRect(0, 0, size, size);

  // Dots — 4×4 сетка с alternating offset (более органичный паттерн чем
  // строгая решётка). Step = 8px, radius = 2px.
  const step = 8;
  const radius = 2;
  ctx.fillStyle = cropColor;
  for (let row = 0; row < 4; row++) {
    const offsetX = row % 2 === 0 ? 0 : step / 2;
    for (let col = 0; col < 4; col++) {
      const x = col * step + offsetX + step / 2;
      const y = row * step + step / 2;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  return ctx.getImageData(0, 0, size, size);
}

/**
 * Загружает PNG-иконку культуры по URL и регистрирует в map image atlas.
 * Обработка ошибок мягкая (логируем + fallback на цветной круг), чтобы одна
 * битая или 404-иконка не ломала рендер карты — symbol-layer всё равно получит
 * валидный image.
 */
function loadAndAddImage(map: MLMap, id: string, url: string, fallbackColor: string): void {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      if (!map.hasImage(id)) {
        map.addImage(id, img);
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn(`[cropPatterns] addImage(${id}) failed`, e);
    }
  };
  img.onerror = () => {
    // Fallback — цветной круг (см. generateFallbackCropIcon). Лишь бы layer
    // не дёргал styleimagemissing event'ом и не плевался warning'ами.
    if (!map.hasImage(id)) {
      try {
        const data = generateFallbackCropIcon(fallbackColor);
        map.addImage(id, data);
      } catch (e) {
        if (import.meta.env.DEV) console.warn(`[cropPatterns] fallback icon ${id} failed`, e);
      }
    }
  };
  img.src = url;
}

/**
 * Fallback-иконка культуры — прозрачный 1×1 px (AK-82, founder feedback
 * 2026-05-04). Используется когда у crop в reference нет icon_url или когда
 * PNG не загрузился (404, CORS, network), а также в `styleimagemissing`
 * handler.
 *
 * Раньше (AK-80) был сплошной 32×32 цветной круг с белой каймой → на zoom
 * 14-16 при `icon-size: 0.5..0.9` рендерился ~16-29px и перекрывал NDVI/GNR
 * raster overlay. Founder: «что это за круги разноцветные».
 *
 * Прозрачная 1×1 иконка решает обе задачи:
 *   1. MapLibre не emit'ит «Image could not be loaded» warning (для этого
 *      AK-80 и делался).
 *   2. Нет визуального мусора поверх растра.
 *
 * Реальные иконки культур (с `icon_url`) загружаются как раньше — fallback
 * срабатывает только для культур БЕЗ зарегистрированной PNG. Backfill icon_url
 * в `reference.crops` для оставшихся 9 культур — отдельная задача.
 *
 * `cropColor` параметр оставлен в сигнатуре для backwards-compat
 * (`registerMissingCropIconFallback` всё ещё передаёт его), но больше не
 * используется. Можно убрать в follow-up рефакторинге.
 */
export function generateFallbackCropIcon(_cropColor: string): ImageData {
  // Полностью прозрачный пиксель — RGBA = 0,0,0,0.
  return new ImageData(new Uint8ClampedArray([0, 0, 0, 0]), 1, 1);
}

/**
 * Регистрирует fallback-иконку для конкретного crop_id (когда reference его
 * не содержит — например, MVT отдала культуру вне scope-aware list'а).
 * Идемпотентна. Цвет — нейтральный серый, fall-back самого fall-back.
 */
export function registerMissingCropIconFallback(
  map: MLMap,
  cropId: string,
  color: string = '#9CA3AF',
): void {
  const id = `crop-icon-${cropId}`;
  if (map.hasImage(id)) return;
  try {
    const data = generateFallbackCropIcon(color);
    map.addImage(id, data);
  } catch (e) {
    if (import.meta.env.DEV) console.warn(`[cropPatterns] registerMissingCropIconFallback(${cropId}) failed`, e);
  }
}

/**
 * Регистрирует обработчик `styleimagemissing` — MapLibre его emit'ит когда
 * symbol-layer пытается отрисовать `crop-icon-{uuid}` которого нет в atlas'е.
 *
 * Без обработчика MapLibre логирует «Image … could not be loaded» в console
 * (см. AK-80). С ним — генерируем fallback-иконку on-the-fly из цвета культуры
 * по reference (если есть) или нейтрального серого. Возвращает unsubscribe.
 *
 * cropsLookup — ленивая функция (не value), чтобы handler всегда читал
 * актуальный reference (TanStack Query refetch / scope change).
 */
export function attachCropIconMissingHandler(
  map: MLMap,
  cropsLookup: () => Crop[] | undefined,
): () => void {
  const PREFIX = 'crop-icon-';
  const handler = (e: { id: string }): void => {
    if (!e.id.startsWith(PREFIX)) return;
    const cropId = e.id.slice(PREFIX.length);
    if (!cropId) return;
    if (map.hasImage(e.id)) return;
    const crops = cropsLookup() ?? [];
    const crop = crops.find((c) => c.id === cropId);
    const color = crop?.color ?? '#9CA3AF';
    registerMissingCropIconFallback(map, cropId, color);
  };
  map.on('styleimagemissing', handler);
  return () => {
    map.off('styleimagemissing', handler);
  };
}

/**
 * Регистрирует все паттерны и иконки для списка культур.
 * Идемпотентен — повторные вызовы безопасны (проверяет hasImage).
 */
export function buildCropPatternAtlas(map: MLMap, crops: Crop[]): void {
  for (const crop of crops) {
    const stripeId = `crop-stripe-${crop.id}`;
    const dotsId = `crop-dots-${crop.id}`;

    if (!map.hasImage(stripeId)) {
      try {
        const data = generateStripePattern(crop.color);
        map.addImage(stripeId, data);
      } catch (e) {
        if (import.meta.env.DEV) console.warn(`[cropPatterns] generate planned pattern for ${crop.id} failed`, e);
      }
    }
    if (!map.hasImage(dotsId)) {
      try {
        // Founder 2026-05-04 v6: harvested = точки (раньше были полосы стерни).
        const data = generateCropDotsPattern(crop.color);
        map.addImage(dotsId, data);
      } catch (e) {
        if (import.meta.env.DEV) console.warn(`[cropPatterns] generate harvested-dots pattern for ${crop.id} failed`, e);
      }
    }
    const iconId = `crop-icon-${crop.id}`;
    if (!map.hasImage(iconId)) {
      if (crop.icon_url) {
        // Если PNG не загрузится (404 / CORS) — onerror зарегистрирует
        // fallback-circle цветом культуры.
        loadAndAddImage(map, iconId, crop.icon_url, crop.color);
      } else {
        // У культуры нет иконки в reference — сразу fallback-circle.
        // Без этого MapLibre будет каждый рендер emit'ить styleimagemissing
        // и spam'ить «Image crop-icon-{uuid} could not be loaded».
        try {
          const data = generateFallbackCropIcon(crop.color);
          map.addImage(iconId, data);
        } catch (e) {
          if (import.meta.env.DEV) console.warn(`[cropPatterns] fallback icon for ${crop.id} failed`, e);
        }
      }
    }
  }
}
